import { debugXHR } from './handlers/xhr'
import { debugPromise } from './handlers/promise'
import { debugConsole } from './handlers/console'
import Request from './utils/request'
import { combineURLs, resetHost } from './utils'
import { EventName } from './utils/CustomErrorEvent'
import { debugGlobalError } from './handlers/error'
import browser, { platform, ua } from './utils/browser'

const LOCAL_ERROR_LOG_LIST = 'LOCAL_ERROR_LOG_LIST'

const getTimestamp = () => {
    return (new Date()).getTime()
}

const outdatedTime = 259200000 // 3 * 24 * 60 * 60 * 1000

/**
 * 错误等级，紧急程度逐次递增
 * @typedef { ( '1' | '2' | '3' | '4' | '5' ) } ErrorLevel
 * */

/**
 * 应用类型
 * @typedef { 'h5sdk' | 'wxSDK' | 'android' | 'ios' | 'admin' | 'tf-admin'}  AppType
 * */

/**
 * @typedef errorCollectDataType
 * @property { number } [ add_time ]    - 当前时间戳
 * @property { string } [ add_date ]    - 当前时间 YYYY-MM-DD

 * @property { string } [ os ]  - 系统
 * @property { string } [ ua ]  - userAgent
 * @property { string } [ platform ]    - 平台标识 -1 未知，0 官网，1 安卓，2 iOS，3 H5游戏，4 微信小游戏
 * @property { 'ios' | 'android' } [ sys_platform ] - 系统类型，小写 ios | android

 * @property { string } [ device_code ] - 设备mac
 * @property { string } [ mobile_type ] - 手机型号
 * @property { string } [ network_type ]    - 网络类型
 * @property { string } [ muid ]    - 设备标识

 * @property { string } [ app_channel ] - 应用渠道
 * @property { string } [ game_id ] - 游戏ID
 * @property { string } [ server_id ]   - 分服ID
 * @property { string } [ gamer_version ]   - 游戏版本
 * @property { string } [ sdk_version ] - SDK版本

 * @property { string } [ username ]    - 账户名
 * @property { string } [ role_id ] - 角色ID
 * @property { string } [ level ]   - 角色等级
 * @property { 0 | 1 } [ channel_exists ]  - 渠道是否已存在 1 存在，0 不存在

 * @property { AppType } [ app_type ]    - 应用类型，h5sdk wxsdk ios pc admin tf
 * @property { string } [ error_time ]  - 异常触发时间
 * @property { string } [ error_type ]  - 异常类型
 * @property { string } [ error_msg ]   - 异常信息
 * @property { ErrorLevel } [ error_lv ]    -  异常级别
 * */

const SysPlatform = browser.isiOS ? 'ios' : browser.isiPad
    ? 'ipad'
    : browser.isAndroid ? 'android' : browser.isMobile ? 'mobile' : 'others'

export default class ErrorCollect {
    baseData = {}

    hostList = []

    saveLogApi = ''

    localErrorLog = {}

    /**
     * @param { string[] } params.hostList
     * @param { any } [params.collectConfig]
     * @param { errorCollectDataType } params.baseData
     * */
    constructor(params = {
        hostList: [],
        collectConfig: {
            'error': {},
            'console': {},
            'xhr': {},
            'promise': {},
        },
        baseData: {},
    }) {
        this.hostList = params.hostList
        this.updateSaveLogApi.bind(this)
        this.updateBaseData.bind(this)
        this.resetApiOrder.bind(this)
        this.sendLog.bind(this)
        this.collect.bind(this)
        debugGlobalError()
        debugXHR({})
        debugPromise()
        debugConsole()

        this.updateSaveLogApi(params.hostList[0])
        this.updateBaseData(params.baseData)

        this.localErrorLog = this.getLocalErrorLog()

        const localLogList = Object.values(this.localErrorLog)

        if (localLogList.length > 0) {
            localLogList.forEach(logData => {
                this.sendLog(logData)
            })
        }

        window.addEventListener(EventName, (event) => {
            console.log('got error', event.detail)
            this.collect(event.detail)
        })
    }

    updateSaveLogApi(host = '//apixk.3698k.com') {
        this.saveLogApi = combineURLs(resetHost(host), '/ErrorLog/errorLog')
    }

    updateBaseData(newData = {}) {
        this.baseData = {
            ...this.baseData,
            ...newData,
        }
    }

    resetApiOrder() {}

    sendLog = (data = {}) => {
        /**
         * 先判断是否联网状态
         * */
        const that = this
        const isOnline = navigator.onLine
        const {
            error_time = getTimestamp(),
            error_type = 'UNKNOWN_ERROR',
            error_detail = {},
            error_msg = '',
            ...restData
        } = data
        const { appConfig = {} } = that.baseData
        const ErrorLog = {
            error_type,
            sys_platform: SysPlatform,
            platform,
            ua,
            game_id: appConfig.game_id,
            ...appConfig,
            ...restData,
            ...that.baseData,
            error_msg: JSON.stringify({
                ...that.baseData,
                ...error_detail,
                error_msg,
            }),
            error_time: `${error_time}`.length > 10
                ? Number(`${error_time}`.substring(0, 10))
                : Number(error_time),
        }
        let filedTime = 0
        // 是否为本地错误日志
        const isLocalLog = this.localErrorLog[error_time] !== undefined
        // 是否为过期日志（时间超过3天）
        const isOutdatedLog = getTimestamp() -
            Number(ErrorLog.error_time * 1000) >=
            outdatedTime
        if (isOutdatedLog) {
            isLocalLog && delete this.localErrorLog[error_time]
            this.updateLocalErrorLog()
        } else {
            const uploadErrorLog = () => {
                if (isOnline) {
                    // 页面联网时正常执行上传错误日志动作
                    Request.post(that.saveLogApi, ErrorLog).then(res => {
                        // 完成后删除该本地日志
                        console.log('that.localErrorLog[error_time]',
                            that.localErrorLog[error_time])
                        if (isLocalLog) {
                            delete that.localErrorLog[error_time]
                            that.updateLocalErrorLog()
                        }
                    }).catch(() => {
                        if (filedTime < 5) {
                            filedTime += 1
                            uploadErrorLog()
                        } else {
                            that.saveLocalErrorLog(ErrorLog)
                        }
                    })
                } else {
                    // 将错误日志保存到本地，当联网时继续上传
                    that.saveLocalErrorLog(ErrorLog)
                    // this.localErrorLog[error_time] = ErrorLog
                    // this.updateLocalErrorLog()
                }
            }
            uploadErrorLog()
        }
    }

    /**
     * 事件参数
     * @param { errorCollectDataType } eventData   - 异常类型
     * */
    collect = (eventData) => {
        this.sendLog(eventData)
    }

    /**
     * 保存未能提交的日志到本地
     * */
    saveLocalErrorLog(data) {
        const { error_time = getTimestamp() } = data
        this.localErrorLog[error_time] = data
        this.updateLocalErrorLog()
    }

    /**
     * 更新提交失败的日志列表
     * */
    updateLocalErrorLog() {
        localStorage.setItem(LOCAL_ERROR_LOG_LIST,
            JSON.stringify(this.localErrorLog))
    }

    /**
     * 检测本地保存的未提交日志
     * */
    getLocalErrorLog() {
        if (localStorage.getItem(LOCAL_ERROR_LOG_LIST)) {
            return JSON.parse(localStorage.getItem(LOCAL_ERROR_LOG_LIST))
        } else {
            return {}
        }
    }
}
